/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect as useLayoutEffect } from 'react-use';
import tw, { css, theme } from 'twin.macro';
import { languageNameTranslations } from 'i18n';
import { purpleHazeTextGradient } from 'styles';

type SiteSearchWidgetProps = {
	DatoCmsSearch: any;
	token?: string;
	buildTriggerId?: string;
	locale?: string;
	locales?: string[];
	perPage?: number;
} & React.ComponentPropsWithoutRef<'div'>;

const SiteSearchWidget: React.FC<SiteSearchWidgetProps> = ({
	DatoCmsSearch,
	token = process.env.NEXT_PUBLIC_DATOCMS_SITESEARCH_TOKEN,
	buildTriggerId,
	id = 'datocms-search-container',
	locale = 'de',
	locales,
	perPage,
	...divProps
}: SiteSearchWidgetProps) => {
	const containerRef = useRef<HTMLDivElement>(null);

	const [loaded, setLoaded] = useState(false);

	// load the widget with dynamically injected DatoCmsSearch module
	useEffect(() => {
		const localesWithoutSelf = locales?.filter((l) => l !== locale);
		const labelledLocales =
			localesWithoutSelf &&
			[locale, ...localesWithoutSelf].map((l) => ({
				label: languageNameTranslations[l],
				value: l,
			}));
		const search = new DatoCmsSearch(token, buildTriggerId);
		search.addWidget(`#${id}`, {
			perPage,
			initialLocale: locale,
			locales: labelledLocales,
		});
		setLoaded(true);
	}, []);

	// localization by 'Dirty Hacks Inc.' after widget has loaded
	useLayoutEffect(() => {
		if (loaded && containerRef.current) {
			const searchInput = containerRef.current.querySelector<HTMLInputElement>(
				'.datocms-widget__search__input'
			);
			const noResultsLabel: any = containerRef.current.querySelector(
				'.datocms-widget__no-results__label'
			);
			const localeFindResultsLabel: any = containerRef.current.querySelector(
				'.datocms-widget__locales__active__label'
			);
			const translated = translations[locale];
			if (searchInput && translated) {
				searchInput.placeholder = translated.searchInput;
			}
			if (noResultsLabel && translated) {
				noResultsLabel.innerText = translated.noResults;
			}
			if (localeFindResultsLabel && translated) {
				localeFindResultsLabel.innerText = `${translated.findInLocale} `;
			}
		}
	}, [loaded]);

	return <div ref={containerRef} id={id} css={stitchesCSS} {...divProps}></div>;
};

export default SiteSearchWidget;

type TranslationType = {
	searchInput: string;
	noResults: string;
	findInLocale: string;
};
const translations: Record<string, TranslationType> = {
	en: {
		searchInput: 'What are you looking for?',
		noResults: 'Please search something! :)',
		findInLocale: 'Find results in',
	},
	de: {
		searchInput: 'Wonach suchen Sie?',
		noResults: 'Bitte suchen Sie etwas! :)',
		findInLocale: 'Ergebnisse finden in',
	},
	fr: {
		searchInput: 'Que cherchez-vous ?',
		noResults: "S'il vous plaît, cherchez quelque chose ! :)",
		findInLocale: 'Trouver des résultats en',
	},
	es: {
		searchInput: '¿Qué buscas?',
		noResults: '¡Por favor, busquen algo! :)',
		findInLocale: 'Buscar resultados en',
	},
	it: {
		searchInput: 'Cosa stai cercando?',
		noResults: 'Per favore, cercate qualcosa! :)',
		findInLocale: 'Trova i risultati in',
	},
	nl: {
		searchInput: 'Waar ben je naar op zoek?',
		noResults: 'Zoek alsjeblieft iets! :)',
		findInLocale: 'Zoek resultaten in het',
	},
	pt: {
		searchInput: 'O que é que procura?',
		noResults: 'Por favor, procure algo! :)',
		findInLocale: 'Encontrar resultados em',
	},
};

// based from https://unpkg.com/datocms-search@0.1.8/styles/index.css
const stitchesCSS: CSS = {
	'.datocms-widget': { margin: '0', width: '100%' },
	'.datocms-widget__result': {
		marginBottom: '15px',
		borderLeft: '3px solid transparent',
		paddingLeft: '15px',
		borderLeftColor: theme`colors.gray.200`,
	},
	'@media screen and (min-width: 600px)': [
		{ '.datocms-widget__result': { paddingRight: '18px' } },
		{
			'.datocms-widget__body': {
				minHeight: '80px',
				maxHeight: '60vh',
				overflow: 'auto',
			},
		},
		{
			'.datocms-widget__header__inner': {
				boxShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
				display: 'flex',
				alignItems: 'center',
			},
			'.datocms-widget__search': { flex: 1 },
			'.datocms-widget__locales': {
				position: 'relative',
				...tw`bg-gray-50`,
			},
			'.datocms-widget__locales__menu': {
				position: 'absolute',
				top: '100%',
				left: '0',
				right: '0',
				backgroundColor: 'white',
				boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
				zIndex: 1000,
			},
			'.datocms-widget__locales__active': {
				whiteSpace: 'nowrap',
				padding: '18px',
				cursor: 'pointer',
				...tw`text-gray-500`,
			},
		},
	],
	'.datocms-widget__result:hover': { borderLeftColor: theme`colors.primary` },
	'.datocms-widget__result__title': {
		display: 'block',
		textDecoration: 'underscore',
		marginBottom: '3px',
		...tw`text-lg text-black font-bold`,
		'.highlight': {
			...tw`font-black`,
			...purpleHazeTextGradient,
		},
	},
	'.datocms-widget__result__body': {
		marginBottom: '3px',
		fontSize: '0.8em',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		...tw`text-base`,
		'.highlight': {
			...tw`font-black`,
			...purpleHazeTextGradient,
		},
	},
	'.datocms-widget__result__url': {
		...tw`text-primary font-semibold text-sm mb-2.5`,
	},
	'.datocms-widget__loading__inner-1, .datocms-widget__loading__inner-2': {
		width: '100%',
		height: '100%',
		borderRadius: '50%',
		backgroundColor: 'rgba(0, 0, 0, 0.2)',
		opacity: 0.6,
		position: 'absolute',
		top: '0',
		left: '0',
		WebkitAnimation: 'datocms-widget-bounce 2.0s infinite ease-in-out',
		animation: 'datocms-widget-bounce 2.0s infinite ease-in-out',
	},
	'.datocms-widget__loading__inner-2': {
		WebkitAnimationDelay: '-1.0s',
		animationDelay: '-1.0s',
	},
	'.datocms-widget__body': { position: 'relative' },
	'.datocms-widget__loading': {
		position: 'absolute',
		top: '50%',
		left: '50%',
		marginTop: '-20px',
		marginLeft: '-20px',
		width: '40px',
		height: '40px',
	},
	'.datocms-widget__no-results__label': {
		textAlign: 'center',
		padding: '40px',
		fontSize: '1.2em',
		...tw`text-gray-500`,
	},
	'.datocms-widget__header': {
		marginBottom: '20px',
		paddingBottom: '20px',
		borderBottom: `1px solid ${theme`colors.primary`}`,
	},
	'.datocms-widget__search__input': {
		width: '100%',
		boxSizing: 'border-box',
		border: '0',
		padding: '18px',
		fontSize: '1rem',
		...tw`shadow-sm rounded-md border border-gray-300`,
		...tw`focus:(ring-inset ring-brand-500 border-brand-500)`,
	},
	'.datocms-widget__locales__menu__item': {
		display: 'block',
		textDecoration: 'none',
		...tw`text-gray-900 p-3.5 hover:bg-gray-50`,
	},
	'.datocms-widget__locales__menu__item:hover': { backgroundColor: '#f3f3f3' },
	'.datocms-widget__locales__active::after': {
		content: '""',
		display: 'inline-block',
		verticalAlign: 'middle',
		marginLeft: '10px',
		width: '0',
		height: '0',
		borderLeft: '6px solid transparent',
		borderRight: '6px solid transparent',
		borderTop: '6px solid #555',
	},
	'.datocms-widget__locales__active__value': {
		...tw`text-gray-900 font-bold`,
	},
	'@media screen and (max-width: 599px)': {
		'.datocms-widget__search__input': {
			marginBottom: '20px',
			boxShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
		},
		'.datocms-widget__locales': { position: 'relative' },
		'.datocms-widget__locales__menu': {
			position: 'absolute',
			top: '100%',
			left: '0',
			marginTop: '10px',
			right: '0',
			backgroundColor: 'white',
			boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
			zIndex: 1000,
		},
	},
	'.datocms-widget__footer': {
		marginTop: '15px',
		paddingTop: '15px',
		borderTop: `1px solid ${theme`colors.primary`}`,
		fontSize: '0.9em',
		display: 'flex',
		alignItems: 'center',
		...tw`text-gray-500`,
	},
	'.datocms-widget__total': { textAlign: 'right', padding: '5px' },
	'.datocms-widget__pagination': { flex: 1 },
	'.datocms-widget__pagination__page': {
		display: 'inline-block',
		padding: '5px',
		marginBottom: '3px',
		color: theme`colors.primary`,
	},
	'.datocms-widget__pagination__page:hover': { textDecoration: 'underscore' },
	'.datocms-widget__pagination__page.is-active': {
		textDecoration: 'none',
		...tw`text-gray-500`,
	},
	'@keyframes datocms-widget-bounce': {
		'0%, 100%': { transform: 'scale(0.0)', WebkitTransform: 'scale(0.0)' },
		'50%': { transform: 'scale(1.0)', WebkitTransform: 'scale(1.0)' },
	},
};
